import axios from 'axios';

import { ADD_USER } from '../../../constants/add-ons';
import { PlanProfiles } from '../../../enums/plan-profiles';
import { ActivationTiming } from '../payment-and-subscription/components/manage-subscription-drawer/constants';

export const getPlanInfo = (includeUsage = false) => {
  const query = `?includeUsage=${Boolean(includeUsage)}`;
  return axios.get(`/account/planInfo${query}`);
};

export const getBillingInfo = () => {
  return axios.get('/account/billingInfo');
};

export const manageSubscription = () => {
  return axios.get('/account/managesubscription').then((response) => {
    const {
      currentBillingPlan, // returns as null
      allPlans = [],
      planToNonWalletAccessPlanMap = {},
      planProfileCode,
    } = response.data;

    if (!currentBillingPlan?.name) {
      return response;
    }

    // low level patch to replace overlapping plan names inside the allPlans data
    // the reason for this is so that a user can make subscription changes WITHOUT being forced into changing to the "default" plan-by-name if they're on a different one
    // practical example: we don't want a High Volume 3.5,4,4.5cent seller to be accidentally switched into the 5cent plan
    // however, we don't want to do this unintentionally, such as when Starter accidentally had the same name across GBB and non-GBB plans
    const isPlanOverwriteAllowed =
      !planProfileCode || (planProfileCode && planProfileCode !== PlanProfiles.goodbetterbest_2025_q2.planProfileCode);

    const matchingPlanByNameIndex = allPlans.findIndex((plan) => plan.name === currentBillingPlan.name);
    // patch this over if it's not a BYOA-replacement plan and is not a GBB plan (overwriting undesired since we have no duplicate plan names)
    if (matchingPlanByNameIndex !== -1 && isPlanOverwriteAllowed) {
      const matchedPlan = allPlans[matchingPlanByNameIndex];
      const isByoaReferencePlan = !!planToNonWalletAccessPlanMap[matchedPlan.billingPlanId];
      if (!isByoaReferencePlan) {
        allPlans[matchingPlanByNameIndex] = currentBillingPlan;
      }
    }

    return response;
  });
};

export const changePlan = (payload) => {
  const isUsingV1Endpoint = !payload?.changePlanRequest;
  if (!isUsingV1Endpoint) {
    return axios.post('/account/v2/changePlan', payload);
  }

  const v2payload = {
    changePlanRequest: {
      newPlanCode: payload.planCode,
      isDowngradeCancellation: payload.isDowngradeCancellation,
    },
    addOnsToPurchase: [],
    addOnsToRemove: [],
    paymentMethodId: payload?.paymentMethodId || null,
  };

  if (Number.isNaN(payload.addOnUsers)) {
    return axios.post('/account/v2/changePlan', v2payload);
  }

  if (payload.addOnUsers > 0) {
    v2payload.addOnsToPurchase = [
      {
        code: ADD_USER,
        quantity: payload.addOnUsers,
        activationTiming: ActivationTiming.IMMEDIATELY,
      },
    ];
  } else {
    v2payload.addOnsToRemove = [
      {
        code: ADD_USER,
        quantity: 0,
        activationTiming: ActivationTiming.IMMEDIATELY,
      },
    ];
  }

  return axios.post('/account/v2/changePlan', v2payload);
};

/**
 * @param {import('./subscription-types.d.ts').ChangePlanRequest} payload
 * @returns {Promise}
 */
export const migrateToPromotionalPlan = (payload) => {
  return axios.post('/account/promotionUpgrade', payload);
};

export const updateAddOns = (payload) => {
  return axios.post('/account/addOns', { addOnUsers: payload });
};

export const getInvoices = () => {
  return axios.get('/account/invoices');
};

export const getArchive = () => {
  return axios.get('/account/monthlyArchive');
};

export const cancelSubscription = (payload) => {
  return axios.post('/account/cancel', payload);
};

export const getCancellationInfo = () => {
  return axios.get('/account/cancel');
};

export const reactivateSubscription = () => {
  return axios.post('/account/reactivatePlan');
};

export const reactivateSubscriptionWithCoupon = (payload) => {
  return axios.post('account/reactivateWithCoupon', payload);
};

export const getFreeStampsSellerProviders = () => {
  return axios.get('/account/getFreeStampsSellerProviders');
};

export const actOnFreeStampsPlans = (cancelOrConvert) => {
  return axios.put(`/account/cancel/stampsPlans/${cancelOrConvert}`);
};
export const updateBillingInfo = (payload) => {
  return axios.post('/account/billingInfo', payload);
};

export const updateBillingInfoWithAdyenSession = (payload) => {
  return axios.post('/account/billingInfoSession', payload);
};

export const updateBillingInfoWithToken = (token, nickname) => {
  return axios.post('/account/billingInfoToken', { token, nickname });
};

export const updateBillingEmail = (email) => {
  return axios.post('/account/billingEmail', { email });
};

export const hasAcceptedNewTermsAndConditions = async (newTermsLabel) => {
  const response = await axios.get(`/seller/accept-terms/${newTermsLabel}`);
  return response.status === 204 ? true : (response?.data ?? true);
};

export const remove3plBundle = () => axios.post('/add-on/disable/FulfillmentProviderBundle');
